import {
  AuthProvider,
  LanguageKey,
  LocaleProvider,
  storageInstance,
} from '@multiplatform-apps/service-trips';
import { currentEnvAppConfig } from '@multiplatform-apps/service-trips';
import { App } from './app/app';
import { ErrorBoundary, RootErrorFallback } from './app/components/error-boundary';
import { translations } from './app/constants';
import {
  ConfirmationProvider,
  FeedbackProvider,
  MenuProvider,
  ModalProvider,
  SessionTimeoutProvider,
} from './app/context';
import { initNewRelic } from './app/hooks/use-new-relic';
import { isForbiddenError, isNotFoundError, isUnauthorizedError } from './app/utils';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import * as ReactDOM from 'react-dom/client';

const ReactQueryDevtools =
  process.env.NODE_ENV !== 'production'
    ? React.lazy(() =>
        import('@tanstack/react-query-devtools').then((m) => ({
          default: m.ReactQueryDevtools,
        }))
      )
    : () => null;

const ENV_DEFAULT_LOCALE = currentEnvAppConfig.defaultLocale as LanguageKey;

const isErrorThatShouldNotRetry = (error: unknown): boolean =>
  isNotFoundError(error) || isForbiddenError(error) || isUnauthorizedError(error);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      retry(failureCount, error) {
        if (isErrorThatShouldNotRetry(error)) {
          // Do not retry on 403, 404, and 401 errors.
          // 401 is handled inside ServiceTripsApi.request() which already does
          // one token-refresh + retry internally — React Query retrying on top
          // is redundant and causes 45 s of hangs when Curity is unreachable.
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const handleLogout = (url: string) => {
  localStorage.removeItem('userFilters');
  window.location.href = url;
};

const getCurrentLanguage = async () =>
  (await storageInstance.getData('currentLanguage')) || ENV_DEFAULT_LOCALE;

// Before render, so the ErrorBoundary below can actually report a crash in the
// provider stack — those happen long before <App/> mounts.
initNewRelic();

getCurrentLanguage().then((currentLanguage) =>
  root.render(
    <QueryClientProvider client={queryClient}>
      <React.Suspense fallback={null}>
        <ReactQueryDevtools initialIsOpen={false} />
      </React.Suspense>
      <LocaleProvider
        translations={translations}
        defaultLanguage={currentLanguage as LanguageKey}
      >
        {/* Providers below sit outside the router, so errorElement cannot catch their crashes. */}
        <ErrorBoundary fallback={(error) => <RootErrorFallback error={error} />}>
          <ModalProvider>
            <ConfirmationProvider>
              <FeedbackProvider>
                <AuthProvider
                  logoutRedirectUri={`${window.location.origin}/sign-in`}
                  handleLogout={handleLogout}
                >
                  <SessionTimeoutProvider>
                    <MenuProvider>
                      <App />
                    </MenuProvider>
                  </SessionTimeoutProvider>
                </AuthProvider>
              </FeedbackProvider>
            </ConfirmationProvider>
          </ModalProvider>
        </ErrorBoundary>
      </LocaleProvider>
    </QueryClientProvider>
  )
);
